// src/utils/menuExcel.js
// Pure helpers for exporting menu prices to Excel and reading an edited sheet back.
// One row per item per size:  Item ID | Item Name | Category | Size | Price (₹) | Price Type
import { SIZES } from '../data/menuData';
import { autoSizePrice } from './money';

export const SHEET_NAME = 'Menu Prices';

export const sizeList = (item) => SIZES[item.sizes] || SIZES.single;

const hasOverride = (item, size) => {
  const v = item.sizePrices?.[size.label];
  return v !== undefined && v !== null && v !== '';
};

// The price actually charged for this item at this size today
export const effectivePrice = (item, size) => {
  const base = Number(item.basePrice) || 0;
  if (size.mult === 1) return base;
  return hasOverride(item, size) ? Number(item.sizePrices[size.label]) : autoSizePrice(base, size.mult);
};

export const buildExportRows = (items) => {
  const sorted = [...items].sort((a, b) =>
    (a.cat || '').localeCompare(b.cat || '') || (a.name || '').localeCompare(b.name || ''));
  const rows = [];
  sorted.forEach(item => sizeList(item).forEach(size => {
    rows.push({
      'Item ID': item.id,
      'Item Name': item.name || '',
      'Category': item.cat || '',
      'Size': size.label,
      'Price (₹)': effectivePrice(item, size),
      'Price Type': size.mult === 1 ? 'Base' : (hasOverride(item, size) ? 'Custom' : 'Auto'),
    });
  }));
  return rows;
};

export const INSTRUCTION_ROWS = [
  ['How to update menu prices'],
  [''],
  ['1. Edit only the "Price (₹)" column on the "' + SHEET_NAME + '" sheet.'],
  ['2. Do not change "Item ID" or "Size" — they are used to match each row to your menu.'],
  ['3. Item Name, Category and Price Type are for reference only; changing them has no effect.'],
  ['4. Base size (e.g. Personal / Small / Regular): enter the price. It cannot be blank.'],
  ['5. Other sizes (e.g. Share / Family): enter a price to set a custom one, or leave the cell blank to switch back to Auto (calculated from the base price).'],
  ['6. Sizes marked Auto follow the base price automatically — you only need to edit them if you want a fixed price.'],
  ['7. Save the file, then in Admin Panel → Menu Items click "Upload Excel". You will see a preview of every change before anything is saved.'],
  ['8. To add new items or change names/categories, use "+ Add Item" in the Admin Panel.'],
];

const norm = (s) => String(s ?? '').trim().toLowerCase();

// rows = sheet rows as objects (from XLSX.utils.sheet_to_json with defval:'')
// Returns { updates, changes, errors, stats }
export const parseImportRows = (rows, items) => {
  const errors = [];
  const changes = [];
  const perItem = new Map(); // id -> { item, baseNew, overrides, touched }
  const seen = new Set();
  let rowsRead = 0, unchanged = 0;

  if (!rows.length) return { updates: [], changes, errors: ['The sheet is empty.'], stats: { rowsRead: 0, unchanged: 0, itemsAffected: 0 } };

  const keys = Object.keys(rows[0]);
  const idKey = keys.find(k => norm(k) === 'item id');
  const sizeKey = keys.find(k => norm(k) === 'size');
  const priceKey = keys.find(k => norm(k).startsWith('price') && !norm(k).includes('type'));
  if (!idKey || !sizeKey || !priceKey) {
    return { updates: [], changes, errors: ['Could not find the "Item ID", "Size" and "Price (₹)" columns. Please use the file downloaded from this page.'], stats: { rowsRead: 0, unchanged: 0, itemsAffected: 0 } };
  }

  const byId = new Map(items.map(i => [String(i.id), i]));

  rows.forEach((row, idx) => {
    const line = idx + 2; // Excel row number (row 1 is the header)
    const id = String(row[idKey] ?? '').trim();
    const sizeTxt = String(row[sizeKey] ?? '').trim();
    const rawPrice = row[priceKey];
    if (!id && !sizeTxt && String(rawPrice ?? '').trim() === '') return; // blank row
    rowsRead++;

    const item = byId.get(id);
    if (!item) { errors.push(`Row ${line}: item ID "${id}" not found in your menu.`); return; }
    const size = sizeList(item).find(s => norm(s.label) === norm(sizeTxt));
    if (!size) { errors.push(`Row ${line}: "${item.name}" has no size called "${sizeTxt}".`); return; }
    const key = id + '|' + size.label;
    if (seen.has(key)) { errors.push(`Row ${line}: "${item.name}" (${size.label}) appears more than once.`); return; }
    seen.add(key);

    const priceStr = String(rawPrice ?? '').trim().replace(/[₹,\s]/g, '');
    const blank = priceStr === '';
    const price = blank ? null : Number(priceStr);
    if (!blank && (!Number.isFinite(price) || price < 0)) {
      errors.push(`Row ${line}: "${rawPrice}" is not a valid price for "${item.name}" (${size.label}).`); return;
    }

    if (!perItem.has(id)) {
      const ov = {};
      Object.entries(item.sizePrices || {}).forEach(([k, v]) => { if (v !== '' && v != null && !isNaN(v)) ov[k] = Number(v); });
      perItem.set(id, { item, baseNew: null, overrides: ov });
    }
    const rec = perItem.get(id);
    const isBase = size.mult === 1;
    const oldEff = effectivePrice(item, size);

    if (isBase) {
      if (blank) { errors.push(`Row ${line}: the base price for "${item.name}" (${size.label}) cannot be blank.`); return; }
      if (price === oldEff) { unchanged++; return; }
      rec.baseNew = price;
      changes.push({ id, name: item.name, cat: item.cat || '', size: size.label, oldVal: oldEff, newVal: price });
    } else if (blank) {
      if (hasOverride(item, size)) {
        delete rec.overrides[size.label];
        changes.push({ id, name: item.name, cat: item.cat || '', size: size.label, oldVal: oldEff, newVal: 'Auto' });
      } else unchanged++;
    } else {
      if (price === oldEff) { unchanged++; return; } // same as today's price → leave as is
      rec.overrides[size.label] = price;
      changes.push({ id, name: item.name, cat: item.cat || '', size: size.label, oldVal: oldEff, newVal: price });
    }
  });

  const changedIds = new Set(changes.map(c => c.id));
  const updates = [];
  perItem.forEach((rec, id) => {
    if (!changedIds.has(id)) return;
    const data = { sizePrices: rec.overrides };
    if (rec.baseNew !== null) data.basePrice = rec.baseNew;
    updates.push({ id, name: rec.item.name, data });
  });

  return { updates, changes, errors, stats: { rowsRead, unchanged, itemsAffected: updates.length } };
};